<template>
  <div class="admin-dashboard">
    <h1>Admin Dashboard</h1>

    <div v-if="loading">Loading...</div>
    <div v-if="errorMessage" class="error">{{ errorMessage }}</div>

    <user-table
      :users="users"
      :roles="roles"
      :ship-calls="shipCalls"
      :selected-roles="selectedRoles"
      :selected-ship-calls="selectedShipCalls"
      :adding-ship-call="addingShipCall"
      @update-user-role="updateUserRole"
      @add-ship-call="addShipCall"
      @confirm-add-ship-call="confirmAddShipCall"
      @remove-ship-call="removeShipCall"
    />
  </div>
</template>

<script>
import UserTable from './UserTable.vue';

export default {
  name: 'AdminDashboard',
  components: {
    UserTable,
  },
  data() {
    return {
      loading: false,
      errorMessage: '',
      users: [],
      roles: [],
      shipCalls: [],
      selectedRoles: {},
      selectedShipCalls: {},
      addingShipCall: {},
    };
  },
  async created() {
    await this.fetchRoles();
    await this.fetchShipCalls();
    await this.fetchUsers();
  },
  methods: {
    async fetchUsers() {
      this.loading = true;
      try {
        const response = await this.$xhr.get('/api/users?populate=role,assignedCalls');
        this.users = response.data;
        this.users.forEach(user => {
          this.$set(this.selectedRoles, user.id, user.role?.id || null);
          this.$set(this.selectedShipCalls, user.id, user.assignedCalls || []);
        });
      } catch (error) {
        this.errorMessage = 'Failed to load users.';
        console.error('User fetch error:', error);
      } finally {
        this.loading = false;
      }
    },

    async fetchRoles() {
      this.loading = true;
      try {
        const response = await this.$xhr.get('/api/users-permissions/roles');
        this.roles = response.data.roles || [];
      } catch (error) {
        this.errorMessage = 'Failed to load roles.';
        console.error('Roles fetch error:', error);
      } finally {
        this.loading = false;
      }
    },

    async fetchShipCalls() {
      this.loading = true;
      this.errorMessage = '';
      try {
        const response = await this.$xhr.get('/api/ship-calls?pagination[pageSize]=1000&sort=operationDate:desc');
        this.shipCalls = response.data.data.map(call => ({
          id: call.id,
          operationType: call.attributes.operationType,
          operationDate: call.attributes.operationDate,
        }));
      } catch (error) {
        console.error('Error fetching ship calls:', error);
        this.errorMessage = 'Failed to load ship calls.';
      } finally {
        this.loading = false;
      }
    },

    async updateUserRole(userId) {
      this.loading = true;
      try {
        await this.$xhr.put(`/api/users/${userId}`, { role: this.selectedRoles[userId] });
        this.showToast('User role updated successfully', 'success');
        await this.fetchUsers();
      } catch (error) {
        this.errorMessage = 'Failed to update user role.';
        console.error('Role update error:', error);
        this.showToast('Failed to update user role', 'danger');
      } finally {
        this.loading = false;
      }
    },

    async updateUserAssignedCalls(userId) {
      this.loading = true;
      try {
        // Send array of IDs for the relation
        const payload = { assignedCalls: this.selectedShipCalls[userId].map(c => c.id) };
        await this.$xhr.put(`/api/users/${userId}`, payload);
        this.showToast('User ship calls updated successfully', 'success');
      } catch (error) {
        this.errorMessage = 'Failed to update user ship calls.';
        console.error('Calls update error:', error);
        this.showToast('Failed to update ship calls', 'danger');
      } finally {
        this.loading = false;
      }
    },

    addShipCall(userId, type) {
      this.$set(this.addingShipCall, userId, { type, value: null });
    },

    async confirmAddShipCall(userId) {
      const newCallId = this.addingShipCall[userId].value;
      if (!newCallId) return;
      
      const shipCall = this.shipCalls.find(c => c.id === newCallId);
      if (!shipCall) return;

      const updatedCalls = [...(this.selectedShipCalls[userId] || []), shipCall];
      this.$set(this.selectedShipCalls, userId, updatedCalls);
      
      await this.updateUserAssignedCalls(userId);
      this.$delete(this.addingShipCall, userId);
    },

    async removeShipCall(userId, callId) {
      const updatedCalls = (this.selectedShipCalls[userId] || []).filter(call => call.id !== callId);
      this.$set(this.selectedShipCalls, userId, updatedCalls);
      await this.updateUserAssignedCalls(userId);
    },

    showToast(message, variant) {
      this.$bvToast.toast(message, { title: variant === 'success' ? 'Success' : 'Error', variant, solid: true });
    },
  },
};
</script>

<style scoped>
.admin-dashboard {
  padding: 20px;
}

.error {
  color: red;
  margin-bottom: 10px;
}
</style>